import { Fieldset } from "primereact/fieldset";
import { useMemo } from "react";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";

const InactiveFieldset = styled(Fieldset)`
  background-color: rgba(255, 193, 7, 0.18);
  border-radius: 4px;
`;

/** The key the API stores the inactive-coverage message under on `Order.integrationErrors`. */
const ELIGIBILITY_ERROR_KEY = "ELIGIBILITY";

const planStatusOf = (benefitDetails: any): string | undefined =>
  benefitDetails?.response?.planStatus;

/**
 * CLE-2803 — the monthly insurance check reported this estimate's coverage inactive and moved it to
 * review. Informational only: it says what the check found, that the patient has already been texted
 * to call, and what the staff member should do about it. There is no action on the banner itself —
 * the resolution is the ordinary one, correcting the insurance below and approving the draft.
 *
 * Shown only when BOTH signals agree — the `ELIGIBILITY` integration error the re-run wrote and a
 * stored response that actually says `Inactive`. The error alone is also written by the importers for
 * a plain eligibility failure, which is a different situation with a different fix.
 */
export function InactiveCoverageBanner({ order }: { order: IOrder }) {
  const { primaryInactive, secondaryInactive } = useMemo(
    () => ({
      primaryInactive: planStatusOf(order?.benefitDetails) === "Inactive",
      secondaryInactive:
        planStatusOf(order?.secondaryBenefitDetails) === "Inactive",
    }),
    [order]
  );

  const hasEligibilityError = Boolean(
    (order?.integrationErrors as { [key: string]: string } | undefined)?.[
      ELIGIBILITY_ERROR_KEY
    ]
  );

  if (!hasEligibilityError || (!primaryInactive && !secondaryInactive)) {
    return null;
  }

  const inactiveSides = [
    primaryInactive ? "primary" : null,
    secondaryInactive ? "secondary" : null,
  ].filter(Boolean);
  const sideLabel = `${inactiveSides.join(" and ")} insurance`;

  return (
    <InactiveFieldset
      className="orderType mb-3"
      legend="Insurance reported inactive"
      data-testid="inactive-coverage-banner"
    >
      <p className="text-balance">
        The monthly insurance check found this patient&apos;s {sideLabel}{" "}
        inactive, so the estimate was moved here for review. The patient has
        been texted to call and update their insurance.
      </p>
      <p className="text-balance">
        Once they do, correct the payer or member details below, run eligibility
        again, and approve the estimate as usual. The estimate has not been
        re-priced and nothing has been sent to the patient beyond that text.
      </p>
    </InactiveFieldset>
  );
}
